import type { SkinDefaultsData } from '@wix/editor-elements-types/thunderbolt';
import { cssPropertyToDefaultValueMap, REGULAR_STATE } from '../constants';
import type { ComponentPartWithStates, PropDefinition } from './types';
import type { SetOptional } from '../types';

export interface SiteAssetsSkinParam {
  type: string;
  defaultValue?: string;
}
type IComponentSiteAssetsSkinParams = Record<
  string,
  Record<string, SiteAssetsSkinParam>
>;

export function generateSkinDefaults(allSkins: IComponentSiteAssetsSkinParams) {
  return Object.entries(allSkins).reduce(
    (acc, [skinName, skinParams]) => {
      acc[skinName] = Object.entries(skinParams).reduce(
        (skinDefaults, [paramName, def]) => {
          const { type, defaultValue } = def;
          skinDefaults.types[paramName] = type;
          if (defaultValue) {
            skinDefaults.defaults[paramName] = defaultValue;
          }
          return skinDefaults;
        },
        { types: {}, defaults: {} } as SkinDefaultsData,
      );
      return acc;
    },
    {} as Record<string, SkinDefaultsData>,
  );
}

export function generateDefaultStyleProperties(
  allSkins: IComponentSiteAssetsSkinParams,
  mapToInitial?: boolean,
) {
  return Object.entries(allSkins).reduce(
    (acc, [skinName, skinParams]) => {
      acc[skinName] = Object.entries(skinParams).reduce(
        (styleProperties, [paramName, def]) => {
          styleProperties[paramName] = mapToInitial
            ? 'initial'
            : ((def.defaultValue as string) ?? 'initial');
          return styleProperties;
        },
        {} as Record<string, string>,
      );
      return acc;
    },
    {} as Record<string, Record<string, string>>,
  );
}

export const withDefaultValues = (params: Record<string, PropDefinition>) => {
  return Object.keys(params).reduce(
    (acc, key) => {
      return {
        ...acc,
        [key]: {
          ...params[key],
          defaultValue:
            params[key].defaultValue || cssPropertyToDefaultValueMap[key],
        },
      };
    },
    {} as Record<string, PropDefinition>,
  );
};

export const extractDefaultValues = (
  skinDefinition: Record<string, PropDefinition>,
) => {
  const defaults = withDefaultValues(skinDefinition);

  return Object.keys(defaults).reduce((acc, key) => {
    return {
      ...acc,
      [key]: skinDefinition[key].defaultValue || 'initial',
    };
  }, {});
};

export const withParts = (
  parts: Array<string>,
  params: Record<string, PropDefinition>,
) => {
  return Object.keys(params).reduce((acc, key) => {
    const newKeys = parts.map(part => `${part}-${key}`);

    return {
      ...acc,
      ...newKeys.reduce(
        (keysAcc, partKey) => ({
          ...keysAcc,
          [partKey]: params[key],
        }),
        {},
      ),
    };
  }, {}) as Record<string, PropDefinition>;
};

export const withStates = (
  stateNames: Array<string>,
  params: Record<string, PropDefinition>,
  defaultState?: string,
) => {
  return stateNames.reduce(
    (acc, stateName) => {
      // UDP expects that default state
      // will be without part prefix in the property name
      const result =
        stateName === defaultState ? params : withParts([stateName], params);

      Object.entries(result).forEach(([key, value]) => {
        result[key] = {
          ...value,
          defaultValue:
            stateName === defaultState ? value.defaultValue : undefined,
          state: stateName,
        };
      });

      return {
        ...acc,
        ...result,
      };
    },
    {} as Record<string, PropDefinition>,
  );
};

export const withPrefixes = (
  params: Record<string, PropDefinition>,
  parts: Array<SetOptional<ComponentPartWithStates, 'label'>>,
) =>
  parts.reduce(
    (acc, part) => ({
      ...acc,
      ...withParts(
        [part.id],
        part.states && part.states.length > 0
          ? withStates(part.states, params, part.defaultState)
          : params,
      ),
    }),
    {} as Record<string, PropDefinition>,
  );

export const fontSkinParams = {
  font: {
    type: 'FONT',
    state: REGULAR_STATE.id,
  },
  color: {
    type: 'CSS_COLOR',
    state: REGULAR_STATE.id,
  },
  'letter-spacing': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'line-height': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'text-outline': {
    type: 'TEXT_OUTLINE',
    state: REGULAR_STATE.id,
  },
  'text-shadow': {
    type: 'TEXT_SHADOW',
    state: REGULAR_STATE.id,
  },
  'text-transform': {
    type: 'TEXT_TRANSFORM',
    state: REGULAR_STATE.id,
  },
  'text-highlight': {
    type: 'CSS_COLOR',
    state: REGULAR_STATE.id,
  },
} as const;

export const layoutSkinParams = {
  'padding-bottom': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'padding-top': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'padding-left': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'padding-right': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
} as const;

export const borderSkinParams = {
  'border-left': {
    type: 'BORDER_SIDE',
    state: REGULAR_STATE.id,
  },
  'border-right': {
    type: 'BORDER_SIDE',
    state: REGULAR_STATE.id,
  },
  'border-top': {
    type: 'BORDER_SIDE',
    state: REGULAR_STATE.id,
  },
  'border-bottom': {
    type: 'BORDER_SIDE',
    state: REGULAR_STATE.id,
  },
} as const;

export const cornerSkinParams = {
  'border-top-left-radius': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'border-top-right-radius': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'border-bottom-left-radius': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
  'border-bottom-right-radius': {
    type: 'SIZE',
    state: REGULAR_STATE.id,
  },
} as const;

export const backgroundSkinParams = {
  background: {
    type: 'BACKGROUND_FILL',
    state: REGULAR_STATE.id,
  },
} as const;

export const boxShadowSkinParams = {
  'box-shadow': {
    type: 'BOX_SHADOW',
    state: REGULAR_STATE.id,
  },
} as const;

export const iconSkinParams = {
  iconSize: 'icon-size',
  iconColor: 'icon-color',
  iconRotation: 'icon-rotation',
  iconEnabled: 'icon-display',
} as const;
